import { IPersonalInformationRequest } from '../../../common/api/services/OnboardingApi/types/IPersonalInformationRequest';
import { IWorkPermitRequest } from '../../../common/api/services/OnboardingApi/types/IWorkPermitRequest';
import { IWorkContractRequest } from '../../../common/api/services/OnboardingApi/types/IWorkContractRequest';
import { IBankAccountRequest } from '../../../common/api/services/OnboardingApi/types/IBankAccountRequest';
import {
    IChildAllowancesRequest,
    IChildRequest,
} from '../../../common/api/services/OnboardingApi/types/IChildAllowanceRequest';
import { IClientContractRequest } from '../../../common/api/services/OnboardingApi/types/IClientContract';
import { ISsnInformationRequest } from '../../../common/api/services/OnboardingApi/types/IAvsAhvRequest';
import { IAdditionalInfoItem } from '../../../common/api/services/OnboardingApi/types/IAdditionalInfoRequest';
import { OnboardingRequestStatuses } from '../../../common/api/services/OnboardingApi/enums/OnboardingRequestStatuses';
import { ITaxSource } from '../../../common/api/services/OnboardingApi/types/ITaxSource';
import { IBankAccount } from '../../../common/api/services/SystemInfoApi/types/ISystemInfo';

export type IWorkPermit = Omit<IWorkPermitRequest, 'isDraft'>;
export type TAdditionalInfo = {
    contacts: Required<IAdditionalInfoItem>[];
};
export type TValidationErrorKeys =
    | 'personalInfo'
    | 'workPermit'
    | 'workContract'
    | 'clientContract'
    | 'bankAccount'
    | 'childAllowances'
    | 'ssnInformation'
    | 'additionalInfo'
    | 'taxAtSource'
    | 'dataPayslip';

export type TOnboardingTabStatus = [
    OnboardingRequestStatuses,
    OnboardingRequestStatuses,
    OnboardingRequestStatuses,
    OnboardingRequestStatuses,
    OnboardingRequestStatuses,
    OnboardingRequestStatuses,
    OnboardingRequestStatuses,
    OnboardingRequestStatuses,
    OnboardingRequestStatuses,
    OnboardingRequestStatuses
];

export interface IState {
    consultantId: string;
    publicId: number;
    consultantEmail: string;
    bankAccounts: Array<IBankAccount>;
    consultantStatus: string;
    personalInfo: IPersonalInfo;
    workPermit: IWorkPermit;
    workContract: IWorkContract;
    clientContract: IClientContract;
    bankAccount: IBankAccountRequest;
    childAllowances: IChildAllowances;
    ssnInformation: ISsnInformationRequest;
    additionalInfo: TAdditionalInfo;
    validationError: Record<TValidationErrorKeys, Record<string, string>>;
    tabStatus: TOnboardingTabStatus;
    isOpenOnboardingLastStepModal: boolean;
    isChangeNationality: boolean;
    userNationality: string;
    isFirstRender: boolean;
    isShowClientContractModal: boolean;
    clientContractModalOptions: IClientContractModalOptions[];
    isPublishedBeforeNewClient: boolean;
    isHasDeletedClient: boolean;
    confirmationUrl: string;
    useNewEmail: boolean | null;
    oldConsultantEmail: string;
    initialClientContractOrder: any;
    initialWorkContractOrder: any;
    taxAtSource: ITaxSource;
    dataPayslip: { dataPayslipItems: IDataPayslips };
    note: string;
}

export interface IClientContractModalOptions {
    name: string;
    before: { paymentTerm: string; paymentTermRange: string };
    after: { paymentTerm: string; paymentTermRange: string };
}

export interface IPersonalInfo
    extends Omit<IPersonalInformationRequest, 'maritalStatusDetails' | 'swissAddress' | 'abroadAddress'> {
    phone: string;
    email: string;
    passportExpirationDate: string;
    maritalStatusDetailsMarriageDate: string;
    maritalStatusDetailsSpouseName: string;
    maritalStatusDetailsSpouseLastName: string;
    maritalStatusDetailsSpouseDateOfBirth: string;
    maritalStatusDetailsSpouseNationality: string;
    maritalStatusDetailsIsSpouseSwissResident: boolean;
    maritalStatusDetailsSpouseSSN: string;
    maritalStatusDetailsSpousalIncome: boolean;
    maritalStatusDetailsSpouseWorkCountry: string;
    maritalStatusDetailsSpouseWorkCity: string;
    maritalStatusDetailsSpouseWorkCanton: string;
    maritalStatusDetailsSpouseStartedWorkingFrom: string;
    swissAddressStreet: string;
    swissAddressNumber: number | string;
    swissAddressState: string;
    swissAddressPostCode: string;
    swissAddressCity: string;
    abroadAddressStreet: string;
    abroadAddressPostCode: string;
    abroadAddressCity: string;
    abroadAddressNumber: number | string;
    abroadAddressState: string;
    abroadAddressCountry: string;
    abroadAddressUseAsCorrespondenceLocation: boolean;
    abroadAddressTotalAttendance: string;
    hasCBA?: boolean;
    hasOtherJob?: boolean;
    hasTreasury?: boolean;
    hasAdditionalAccidentInsurance?: boolean;
}

export interface IChildAllowances extends Omit<IChildAllowancesRequest, 'children'> {
    children: IChildren[];
}

export interface IChildren extends IChildRequest {
    id: number;
}

export interface IWorkContract extends IWorkContractRequest {
    selectedClientIdx: number;
    selectedDublicateIdx: number;
}

export interface IClientContract extends IClientContractRequest {
    selectedClientIdx: number;
    isNewClient?: boolean;
}

export interface IOnboardingSaveOptions {
    moveToNextPage?: boolean;
    redirectRootRout?: string;
    useNewEmail?: boolean;
}

export interface IDataPayslip {
    period: string | null;
    category: string;
    amount: number;
    comment: string;
}

export type IDataPayslips = IDataPayslip[];

export enum ITabNames {
    'personalInfo' = 'Personal information',
    'personalInformation' = 'Personal information',
    'workPermit' = 'Work permit',
    'workContract' = 'Work contract',
    'clientContract' = 'Client contract',
    'bankAccount' = 'Bank account',
    'childAllowances' = 'Children',
    'ssnInformation' = 'Pension',
    'additionalInfo' = 'Additional information',
    'taxAtSource' = 'Tax-at-source',
    'dataPayslip' = 'Data Payslip',
}
